import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiHelpers } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, AlertTriangle, ExternalLink } from 'lucide-react';
import type { PluginManifest } from '@/types';
import { useConfirm } from '@/components/ui/confirm-provider';

interface OAuthPropertyPickerProps {
  manifest: PluginManifest;
  projectId: string;
  pluginId: string;
  /** Current plugin config — used to detect whether a property is already selected */
  currentConfig?: Record<string, any>;
}

/**
 * For oauth-integration plugins (Google Analytics, Meta, etc.), this renders
 * a "Select the property to track" step after successful OAuth install.
 *
 * - Shows "Connected as <email>" badge using the userInfo from the OAuth provider.
 * - Lists available properties grouped by account.
 * - On pick: updates plugin config with the chosen property/measurementId.
 *
 * If the config already has a selection, shows a summary + "Change property" button.
 */
export function OAuthPropertyPicker({
  manifest,
  projectId,
  pluginId,
  currentConfig,
}: OAuthPropertyPickerProps) {
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['oauth-properties', projectId, pluginId],
    queryFn: () => apiHelpers.getOAuthProperties(projectId, pluginId),
    staleTime: 60_000,
  });

  const saveMutation = useMutation({
    mutationFn: async (selection: {
      measurementId: string;
      propertyId: string;
      accountName: string;
      propertyDisplayName: string;
    }) => {
      const merged = { ...(currentConfig || {}), ...selection };
      return apiHelpers.updatePluginConfig(projectId, pluginId, { config: merged });
    },
    onSuccess: () => {
      toast.success('Property connected');
      queryClient.invalidateQueries({ queryKey: ['plugin-manifest', projectId, pluginId] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Failed to save property');
    },
  });

  // ── Loading ────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="bg-card flex items-center justify-center rounded-xl border py-10">
        <Loader2 className="text-primary h-5 w-5 animate-spin" />
        <span className="text-muted-foreground ml-2 text-sm">
          Loading properties from {manifest.name}…
        </span>
      </div>
    );
  }

  // ── OAuth error (expired / revoked token) ─────────────────
  if (error) {
    const msg =
      (error as any)?.response?.data?.error?.code === 'OAUTH_EXPIRED'
        ? 'Your OAuth session has expired. Please reconnect to refresh your credentials.'
        : (error as any)?.response?.data?.error?.message || 'Could not fetch properties';

    return (
      <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-5 dark:border-yellow-800 dark:bg-yellow-900/20">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-600 dark:text-yellow-400" />
          <div className="flex-1">
            <p className="font-medium">Connection issue</p>
            <p className="text-muted-foreground mt-1 text-sm">{msg}</p>
            <button onClick={() => refetch()} className="text-primary mt-3 text-sm hover:underline">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const payload = data?.data?.data;
  const userInfo = payload?.userInfo;
  const accounts = payload?.accounts || [];

  // Flatten properties for rendering
  const allProperties = accounts.flatMap((acc) =>
    acc.properties.map((p) => ({ ...p, accountId: acc.accountId, accountName: acc.accountName }))
  );
  const selectedMeasurementId = currentConfig?.measurementId as string | undefined;
  const selectedProperty = allProperties.find((p) => p.measurementId === selectedMeasurementId);

  // ── Connection summary + selected property ────────────────
  if (selectedProperty) {
    return (
      <div className="space-y-3">
        {userInfo && <ConnectedAsBadge userInfo={userInfo} />}
        <div className="bg-card rounded-xl border p-5">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
            <div className="flex-1">
              <p className="font-medium">Tracking {selectedProperty.displayName}</p>
              <p className="text-muted-foreground mt-0.5 text-sm">
                {selectedProperty.accountName} · {selectedProperty.measurementId}
              </p>
              <div className="mt-3 flex gap-3 text-sm">
                <button
                  onClick={async () => {
                    if (
                      await confirm({
                        title: 'Change tracked property?',
                        message: 'Change the tracked property?',
                      })
                    ) {
                      queryClient.setQueryData(
                        ['plugin-manifest', projectId, pluginId],
                        (old: any) => {
                          if (!old) return old;
                          const clone = structuredClone(old);
                          if (clone?.data?.data?.installed?.config) {
                            delete clone.data.data.installed.config.measurementId;
                          }
                          return clone;
                        }
                      );
                    }
                  }}
                  className="text-primary hover:underline"
                >
                  Change property
                </button>
                {manifest.links?.dashboard && (
                  <a
                    href={manifest.links.dashboard}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary inline-flex items-center gap-1 hover:underline"
                  >
                    Open {manifest.name} <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Property selection view ───────────────────────────────
  if (allProperties.length === 0) {
    return (
      <div className="bg-card rounded-xl border p-6 text-center">
        <p className="text-muted-foreground text-sm">No GA4 properties found on this account.</p>
        <p className="text-muted-foreground mt-2 text-xs">
          Create a property in Google Analytics, then come back here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {userInfo && <ConnectedAsBadge userInfo={userInfo} />}

      <div className="bg-card rounded-xl border p-5">
        <p className="font-medium">Select a property to track</p>
        <p className="text-muted-foreground mt-0.5 text-sm">
          Tyashin will inject the tracking snippet on your published site.
        </p>

        <div className="mt-4 space-y-4">
          {accounts.map((acc) => (
            <div key={acc.accountId}>
              <p className="text-muted-foreground mb-2 text-xs font-semibold uppercase tracking-wide">
                {acc.accountName}
              </p>
              <div className="space-y-2">
                {acc.properties.map((prop) => (
                  <button
                    key={prop.propertyId}
                    onClick={() =>
                      saveMutation.mutate({
                        measurementId: prop.measurementId,
                        propertyId: prop.propertyId,
                        accountName: acc.accountName,
                        propertyDisplayName: prop.displayName,
                      })
                    }
                    disabled={saveMutation.isPending || !prop.measurementId}
                    className="hover:bg-muted/50 flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition-colors disabled:opacity-40"
                  >
                    <div>
                      <p className="font-medium">{prop.displayName}</p>
                      <p className="text-muted-foreground mt-0.5 text-xs">
                        {prop.measurementId || 'No web data stream'}
                      </p>
                    </div>
                    {saveMutation.isPending &&
                      saveMutation.variables?.propertyId === prop.propertyId && (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Small helper component
// =============================================================================

function ConnectedAsBadge({
  userInfo,
}: {
  userInfo: { email: string; name?: string; picture?: string };
}) {
  return (
    <div className="bg-muted/30 flex items-center gap-3 rounded-lg px-3 py-2 text-sm">
      {userInfo.picture ? (
        <img
          src={userInfo.picture}
          alt=""
          className="h-6 w-6 rounded-full"
          referrerPolicy="no-referrer"
        />
      ) : (
        <div className="bg-primary/10 text-primary flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold">
          {userInfo.email[0]?.toUpperCase()}
        </div>
      )}
      <div className="flex-1">
        <span className="text-muted-foreground">Connected as </span>
        <span className="font-medium">{userInfo.name || userInfo.email}</span>
        {userInfo.name && <span className="text-muted-foreground ml-1">({userInfo.email})</span>}
      </div>
    </div>
  );
}
